/// FOURJS_START_COPYRIGHT(D,2025)
/// Property of Four Js*
/// (c) Copyright Four Js 2025, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("SideBarSplitterWidget", ["SplitterWidget", "WidgetFactory"], function (context, cls) {
  cls.SideBarSplitterWidget = context.oo.Class(cls.SplitterWidget, function ($super) {
    return /** @lends classes.SideBarSplitterWidget.prototype */ {
      __name: "SideBarSplitterWidget",
      __templateName: "SplitterWidget",
      /**
       * The sidebar reference
       * @type {HTMLElement}
       */
      _sideBarElement: null,
      /**
       * The SideBar width at drag start
       */
      _sideBarInitialWidth: -1,

      _initElement: function () {
        $super._initElement.call(this);
        this.activateSplitter(true);
        cls.DOMElementHelper.updateQAAttribute(this.getElement(), "name", "gbc_sideBarSplitter");
      },

      /**
       * Store the drawer element to manipulate later on
       * @param {HTMLElement} drawer The Drawer HTML Element reference
       */
      setSideBarElement: function (drawer) {
        this._sideBarElement = drawer;
        // The controlled pane is now known: refresh ARIA so aria-controls points at it (W3C window splitter).
        this._updateAriaValues();
      },

      _updateResizerDrag: function (evt) {
        const pageX = evt.pageX ?? evt.touches?.[0]?.pageX ?? 0;
        this._pagePosition = pageX;
        this._resizerDragPosition = pageX;
        this._splitStartPos = pageX;
      },

      _onDragOver: function (evt) {
        $super._onDragOver.call(this, evt);
        this._pagePosition = evt.pageX;
      },

      _onDragStart: function (evt) {
        $super._onDragStart.call(this, evt);
        this._sideBarInitialWidth = this._sideBarElement.offsetWidth;
        cls.DOMElementHelper.on(evt.target.parentElement, "dragover.SplitterWidget", this._onDragOver.bind(this));
      },

      _onDragEnd: function (evt) {
        $super._onDragEnd.call(this, evt);
        this._sideBarInitialWidth = this._sideBarElement.offsetWidth;
        gbc.StoredSettingsService.saveSideBarWidth(this._sideBarInitialWidth);
        cls.DOMElementHelper.off(evt.target.parentElement, "dragover.SplitterWidget");
      },

      _onTouchStart: function (evt) {
        this._splitStartPos = evt.touches[0].clientX;
        $super._onTouchStart.call(this, evt);
      },

      _onTouchMove: function (evt) {
        this._pagePosition = evt.touches[0].clientX;
        $super._onTouchMove.call(this, evt);
      },
      updateSplits: function (delta) {
        if (!Number.isFinite(delta) || this._sideBarInitialWidth === -1) {
          return;
        }

        this._sideBarElement.style.width = `${this._sideBarInitialWidth + delta}px`;
      },

      // --- Accessibility: keyboard resize for the sidebar drawer (GBC-5755). The sidebar uses a width-set
      // path (no layout engine / splitHints), so it overrides the value/step primitives and _performStep. ---

      /**
       * The sidebar splitter always separates left/right -> vertical bar.
       * @inheritDoc
       */
      _getAriaOrientation: function () {
        return "vertical";
      },

      /**
       * The separator controls the pane it resizes (the sidebar drawer or the docked panel); both carry a
       * stable id (main template / restored in MainContainerService), so reference it via aria-controls.
       * @inheritDoc
       */
      _getAriaControlsId: function () {
        return this._sideBarElement?.id || null;
      },

      /**
       * Distinct accessible name so the sidebar separator is not confused with the form box splitters.
       * @inheritDoc
       */
      _getAriaLabel: function () {
        return gbc.I18NService.t("gwc.splitter.sidebarLabel");
      },

      /**
       * @returns {number} maximum drawer width in px (mirrors the CSS max-width: 80% of the container)
       * @private
       */
      _maxWidth: function () {
        const container = this._sideBarElement?.offsetParent || this._sideBarElement?.parentElement;
        const containerWidth = container ? container.clientWidth : 0;
        return containerWidth ? Math.round(containerWidth * 0.8) : this._sideBarElement?.offsetWidth || 0;
      },

      /**
       * @returns {number} minimum drawer width in px (pragmatic floor — the sidebar has no explicit min)
       * @private
       */
      _minWidth: function () {
        return Math.min(200, Math.round(this._maxWidth() * 0.2));
      },

      /**
       * @inheritDoc
       */
      _computeValueNow: function () {
        if (!this._sideBarElement) {
          return null;
        }
        const min = this._minWidth();
        const max = this._maxWidth();
        if (max <= min) {
          return null;
        }
        const pct = ((this._sideBarElement.offsetWidth - min) / (max - min)) * 100;
        return Math.round(Math.min(100, Math.max(0, pct)));
      },

      /**
       * @inheritDoc
       */
      _getStepDelta: function () {
        return Math.max(1, Math.round(0.05 * (this._maxWidth() - this._minWidth())));
      },

      /**
       * @inheritDoc
       */
      _getFullRangePx: function () {
        return this._maxWidth() - this._minWidth();
      },

      /**
       * @inheritDoc
       */
      _primarySizePx: function () {
        return this._sideBarElement ? this._sideBarElement.offsetWidth : 0;
      },

      /**
       * Keyboard step for the sidebar: set the drawer width directly, clamped to [min, max], and persist.
       * (The drag path's `updateSplits` relies on `_sideBarInitialWidth` set at dragstart, absent here.)
       * @inheritDoc
       */
      _performStep: function (delta) {
        if (!this._sideBarElement) {
          return;
        }
        const min = this._minWidth();
        const max = this._maxWidth();
        const target = Math.min(max, Math.max(min, this._sideBarElement.offsetWidth + delta));
        this._sideBarElement.style.width = `${target}px`;
        gbc.StoredSettingsService.saveSideBarWidth(target);
      }
    };
  });

  cls.WidgetFactory.registerBuilder("SideBarSplitter", cls.SideBarSplitterWidget);
});
